/**
 * <s-lazy-video> — Loads and plays a silent looping clip only while it is on screen.
 *
 * Contract:
 *   <s-lazy-video>
 *     <video muted loop playsinline preload="none" data-src="clip.mp4"></video>
 *   </s-lazy-video>
 *
 * The video has no src until the element nears the viewport, so offscreen clips cost nothing.
 * It pauses when scrolled away and never plays when the visitor prefers reduced motion;
 * the cover image behind the video stays visible in those cases.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class LazyVideo extends HTMLElement {
  #observer = null;

  connectedCallback() {
    const video = this.querySelector('video[data-src]');
    if (!video || reducedMotion.matches) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!video.src) video.src = video.dataset.src;
            video.play().catch(() => {});
          } else if (video.src) {
            video.pause();
          }
        }
      },
      { rootMargin: '0px 200px 0px 200px' },
    );
    // Observe the video itself: the host uses display: contents, which has no box to intersect.
    this.#observer.observe(video);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('s-lazy-video')) customElements.define('s-lazy-video', LazyVideo);
